/**
 * Hook for tracking detached window resize/move events
 * Persists position and size to localStorage for restoration on reload
 */

import { useEffect } from 'react';
import { windowManager } from '../services/windowManager';

export function useDetachedWindowResize() {
  useEffect(() => {
    // Only run in detached windows
    const isDetached = new URLSearchParams(window.location.search).get('detached') === 'true';
    const traderId = new URLSearchParams(window.location.search).get('traderId');

    if (!isDetached || !traderId) return;

    const RESIZE_DEBOUNCE = 300; // ms
    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;

    /**
     * Handle window resize/move — persist new dimensions
     */
    const handleResizeMove = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);

      resizeTimeout = setTimeout(() => {
        try {
          const x = window.screenX;
          const y = window.screenY;
          const width = window.outerWidth;
          const height = window.outerHeight;

          // Update in windowManager
          windowManager.updateWindowPosition(traderId, { x, y });
          windowManager.updateWindowSize(traderId, { width, height });

          // Also persist directly to localStorage as backup
          const key = `detached-window-${traderId}`;
          localStorage.setItem(key, JSON.stringify({
            x, y, width, height,
            timestamp: Date.now()
          }));

          console.log(`[DetachedWindow] Saved position: ${x}, ${y}, ${width}x${height}`);
        } catch (err) {
          console.error('[DetachedWindow] Failed to persist window state:', err);
        }
      }, RESIZE_DEBOUNCE);
    };

    // Listen for resize
    window.addEventListener('resize', handleResizeMove);

    // Try to listen for move (not all browsers support this directly)
    // Some browsers fire move events, others require polling screenX/screenY
    window.addEventListener('move', handleResizeMove);

    // Fallback: poll for position changes every 500ms
    const pollInterval = setInterval(() => {
      // Compare current position with last known position
      const storedKey = `detached-window-${traderId}`;
      const stored = localStorage.getItem(storedKey);

      if (stored) {
        try {
          const last = JSON.parse(stored);
          const currentX = window.screenX;
          const currentY = window.screenY;
          const currentW = window.outerWidth;
          const currentH = window.outerHeight;

          // If position/size changed, update
          if (currentX !== last.x || currentY !== last.y ||
              currentW !== last.width || currentH !== last.height) {
            handleResizeMove();
          }
        } catch (err) {
          // Ignore parsing errors
        }
      }
    }, 500);

    return () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      clearInterval(pollInterval);
      window.removeEventListener('resize', handleResizeMove);
      window.removeEventListener('move', handleResizeMove);
    };
  }, []);
}
